// Centralised translations for all 10 supported languages.
//
// Keys are grouped by feature. Use the `t(key)` helper from ./i18n.js
// in components; raw imports of this file are NOT required.
//
// When adding a new string:
//   1. Add its `de` value here (source-of-truth).
//   2. Translate to the 9 other languages.
//   3. The TTS cache will auto-regenerate (cache key includes the text).

export const LANGUAGES = [
    { code: "de", label: "Deutsch" },
    { code: "en", label: "English" },
    { code: "fr", label: "Français" },
    { code: "it", label: "Italiano" },
    { code: "tr", label: "Türkçe" },
    { code: "uk", label: "Українська" },
    { code: "hr", label: "Hrvatski" },
    { code: "bg", label: "Български" },
    { code: "ru", label: "Русский" },
    { code: "ro", label: "Română" },
    { code: "pl", label: "Polski" },
    { code: "sq", label: "Shqip" },
    { code: "da", label: "Dansk" },
    { code: "nl", label: "Nederlands" },
    { code: "fi", label: "Suomi" },
    { code: "et", label: "Eesti" },
    { code: "el", label: "Ελληνικά" },
    { code: "ga", label: "Gaeilge" },
    { code: "lv", label: "Latviešu" },
    { code: "lt", label: "Lietuvių" },
    { code: "mt", label: "Malti" },
    { code: "pt", label: "Português" },
    { code: "sv", label: "Svenska" },
    { code: "sk", label: "Slovenčina" },
    { code: "sl", label: "Slovenščina" },
    { code: "es", label: "Español" },
    { code: "cs", label: "Čeština" },
    { code: "hu", label: "Magyar" },
];

export const DEFAULT_LANGUAGE = "en";
export const SUPPORTED_CODES = LANGUAGES.map((l) => l.code);

export const translations = {
    // ---------------- HEADER ----------------
    header_tagline: {
        de: "Im Notfall immer zuerst",
        en: "In an emergency, always first",
        tr: "Acil durumda her zaman önce",
        uk: "У надзвичайній ситуації завжди спочатку",
        hr: "U hitnom slučaju uvijek prvo",
        bg: "При спешност винаги първо",
        ru: "В экстренной ситуации всегда сначала",
        ro: "În caz de urgență, întotdeauna mai întâi",
        pl: "W nagłym wypadku zawsze najpierw",
        sq: "Në rast urgjence, gjithmonë së pari",
    },
    header_emergency_number: {
        de: "Rettungsdienst {num}",
        en: "Emergency services {num}",
        tr: "Acil servis {num}",
        uk: "Швидка допомога {num}",
        hr: "Hitna služba {num}",
        bg: "Спешна помощ {num}",
        ru: "Служба спасения {num}",
        ro: "Serviciul de urgență {num}",
        pl: "Pogotowie {num}",
        sq: "Shërbimi i urgjencës {num}",
    },
    header_scan_call: {
        de: "Scan & Call",
        en: "Scan & Call",
        tr: "Tara ve Ara",
        uk: "Скануй і телефонуй",
        hr: "Skeniraj i nazovi",
        bg: "Сканирай и звънни",
        ru: "Сканируй и звони",
        ro: "Scanează și sună",
        pl: "Skanuj i dzwoń",
        sq: "Skano dhe telefono",
    },
    header_qr_hint: {
        de: "Notfall-Hilfe per QR",
        en: "Emergency help via QR",
        tr: "QR ile acil yardım",
        uk: "Допомога через QR",
        hr: "Hitna pomoć putem QR-a",
        bg: "Спешна помощ чрез QR",
        ru: "Экстренная помощь через QR",
        ro: "Ajutor de urgență prin QR",
        pl: "Pomoc w nagłych wypadkach przez QR",
        sq: "Ndihmë urgjente nëpërmjet QR",
    },
    header_clear_location: {
        de: "Gespeicherten Standort entfernen",
        en: "Clear saved location",
        tr: "Kayıtlı konumu sil",
        uk: "Видалити збережене місце",
        hr: "Izbriši spremljenu lokaciju",
        bg: "Изтрий запазеното местоположение",
        ru: "Удалить сохранённое местоположение",
        ro: "Șterge locația salvată",
        pl: "Usuń zapisaną lokalizację",
        sq: "Fshi vendndodhjen e ruajtur",
    },

    // ---------------- LANGUAGE PICKER ----------------
    language_label: {
        de: "Sprache",
        en: "Language",
        tr: "Dil",
        uk: "Мова",
        hr: "Jezik",
        bg: "Език",
        ru: "Язык",
        ro: "Limbă",
        pl: "Język",
        sq: "Gjuha",
    },

    // ---------------- LANDING ----------------
    landing_emergency_title: {
        de: "Notfall*",
        en: "Emergency*",
        tr: "Acil durum*",
        uk: "Надзвичайна ситуація*",
        hr: "Hitan slučaj*",
        bg: "Спешност*",
        ru: "Чрезвычайная ситуация*",
        ro: "Urgență*",
        pl: "Nagły wypadek*",
        sq: "Urgjencë*",
    },
    landing_emergency_sub: {
        de: "Starte die sprachgeführte Hilfe.",
        en: "Start the voice-guided help.",
        tr: "Sesli rehberli yardımı başlat.",
        uk: "Почніть голосову допомогу.",
        hr: "Započnite vođenu pomoć glasom.",
        bg: "Започнете гласово напътствана помощ.",
        ru: "Начните голосовую помощь.",
        ro: "Începe ajutorul ghidat vocal.",
        pl: "Rozpocznij pomoc głosową.",
        sq: "Filloni ndihmën me udhëzime me zë.",
    },
    landing_demo_label: {
        de: "Demo",
        en: "Demo",
        tr: "Demo",
        uk: "Демо",
        hr: "Demo",
        bg: "Демо",
        ru: "Демо",
        ro: "Demo",
        pl: "Demo",
        sq: "Demo",
    },
    landing_demo_title: {
        de: "Ich habe keinen Notfall aber ich möchte sehen wie es funktioniert.",
        en: "I don't have an emergency but I want to see how it works.",
        tr: "Acil durumum yok ama nasıl çalıştığını görmek istiyorum.",
        uk: "У мене немає надзвичайної ситуації, але я хочу побачити, як це працює.",
        hr: "Nemam hitan slučaj, ali želim vidjeti kako to radi.",
        bg: "Нямам спешност, но искам да видя как работи.",
        ru: "У меня нет экстренной ситуации, но я хочу посмотреть, как это работает.",
        ro: "Nu am o urgență, dar vreau să văd cum funcționează.",
        pl: "Nie mam nagłego wypadku, ale chcę zobaczyć, jak to działa.",
        sq: "Nuk kam urgjencë, por dua të shoh si funksionon.",
    },
    landing_aed_button: {
        de: "Defibrillator-Karte öffnen",
        en: "Open defibrillator map",
        tr: "Defibrilatör haritasını aç",
        uk: "Відкрити карту дефібриляторів",
        hr: "Otvori kartu defibrilatora",
        bg: "Отвори картата с дефибрилатори",
        ru: "Открыть карту дефибрилляторов",
        ro: "Deschide harta defibrilatoarelor",
        pl: "Otwórz mapę defibrylatorów",
        sq: "Hap hartën e defibrilatorëve",
    },
    landing_aed_button_sub: {
        de: "Defibrillatoren in der Nähe finden",
        en: "Find defibrillators nearby",
        tr: "Yakındaki defibrilatörleri bul",
        uk: "Знайти дефібрилятори поруч",
        hr: "Pronađi defibrilatore u blizini",
        bg: "Намери дефибрилатори наблизо",
        ru: "Найти дефибрилляторы рядом",
        ro: "Găsește defibrilatoare în apropiere",
        pl: "Znajdź defibrylatory w pobliżu",
        sq: "Gjej defibrilatorë afër",
    },
    landing_skip_to_cpr: {
        de: "Springe direkt zur Reanimation",
        en: "Skip directly to resuscitation",
        tr: "Doğrudan canlandırmaya geç",
        uk: "Перейти одразу до реанімації",
        hr: "Skoči izravno na oživljavanje",
        bg: "Премини директно към реанимацията",
        ru: "Перейти сразу к реанимации",
        ro: "Sari direct la resuscitare",
        pl: "Przejdź bezpośrednio do reanimacji",
        sq: "Kalo drejtpërdrejt te ringjallja",
    },
    landing_skip_to_cpr_sub: {
        de: "Im Demo-Modus, ohne Triage-Fragen.",
        en: "In demo mode, without triage questions.",
        tr: "Demo modunda, triyaj soruları olmadan.",
        uk: "У демо-режимі, без тріажних запитань.",
        hr: "U demo načinu rada, bez triage pitanja.",
        bg: "В демо режим, без триажни въпроси.",
        ru: "В демо-режиме, без вопросов триажа.",
        ro: "În mod demo, fără întrebări de triaj.",
        pl: "W trybie demo, bez pytań triażu.",
        sq: "Në modalitetin demo, pa pyetje triazhi.",
    },
    email_popup_title: {
        de: "Bleib auf dem Laufenden",
        en: "Stay in the loop",
        tr: "Güncel kal",
        uk: "Будьте в курсі",
        hr: "Ostanite u tijeku",
        bg: "Бъдете в крак",
        ru: "Оставайтесь в курсе",
        ro: "Rămâi la curent",
        pl: "Bądź na bieżąco",
        sq: "Qëndro i informuar",
    },
    email_popup_body: {
        de: "Schön, dass du SafetyAssist ausprobierst. Wir entwickeln diese Notfall-Hilfe stetig weiter. Trag deine E-Mail ein und erhalte Updates, sobald neue Funktionen verfügbar sind.",
        en: "Glad you're trying SafetyAssist. We keep improving this emergency aid. Leave your e-mail to get updates as soon as new features arrive.",
        tr: "SafetyAssist'i denediğin için teşekkürler. Bu acil yardımı sürekli geliştiriyoruz. Yeni özellikler yayınlandığında haberdar olmak için e-postanı bırak.",
        uk: "Дякуємо, що пробуєте SafetyAssist. Ми постійно покращуємо цю екстрену допомогу. Залиште e-mail, щоб отримувати оновлення, щойно з'являться нові функції.",
        hr: "Drago nam je što isprobavate SafetyAssist. Ovu hitnu pomoć stalno razvijamo. Ostavite e-mail i primit ćete obavijesti o novim značajkama.",
        bg: "Радваме се, че изпробвате SafetyAssist. Постоянно подобряваме тази спешна помощ. Оставете имейл, за да получавате актуализации при нови функции.",
        ru: "Спасибо, что пробуете SafetyAssist. Мы постоянно развиваем эту экстренную помощь. Оставьте e-mail, чтобы получать обновления о новых функциях.",
        ro: "Mă bucur că încerci SafetyAssist. Îmbunătățim continuu acest ajutor de urgență. Lasă-ți e-mailul pentru a primi noutăți când apar funcții noi.",
        pl: "Cieszymy się, że testujesz SafetyAssist. Stale ulepszamy tę pomoc ratunkową. Zostaw swój e-mail i otrzymasz informacje o nowych funkcjach.",
        sq: "Faleminderit që po provoni SafetyAssist. Po e përmirësojmë vazhdimisht këtë ndihmë urgjence. Lini email-in tuaj për të marrë lajmërime kur shfaqen funksione të reja.",
    },
    email_popup_email_placeholder: {
        de: "deine@email.de",
        en: "you@email.com",
        tr: "siz@email.com",
        uk: "ви@email.com",
        hr: "vi@email.com",
        bg: "ви@email.com",
        ru: "вы@email.com",
        ro: "tu@email.com",
        pl: "ty@email.com",
        sq: "ju@email.com",
    },
    email_popup_consent: {
        de: "Mit dem Absenden stimme ich der Speicherung meiner E-Mail-Adresse und der Zusendung von Updates zu SafetyAssist zu.",
        en: "By submitting I agree to my e-mail address being stored and used to send SafetyAssist updates.",
        tr: "Göndererek e-posta adresimin saklanmasına ve SafetyAssist güncellemelerinin gönderilmesine onay veriyorum.",
        uk: "Надсилаючи форму, я погоджуюся на зберігання моєї e-mail адреси та надсилання оновлень про SafetyAssist.",
        hr: "Slanjem pristajem da se moja e-mail adresa pohrani i koristi za slanje SafetyAssist obavijesti.",
        bg: "С изпращането се съгласявам имейлът ми да бъде съхранен и използван за изпращане на актуализации за SafetyAssist.",
        ru: "Отправляя форму, я соглашаюсь на хранение моего адреса электронной почты и получение обновлений SafetyAssist.",
        ro: "Trimițând, sunt de acord ca adresa mea de e-mail să fie stocată și folosită pentru actualizări SafetyAssist.",
        pl: "Wysyłając wyrażam zgodę na zapisanie mojego adresu e-mail i otrzymywanie aktualizacji SafetyAssist.",
        sq: "Duke dërguar, pranoj që adresa ime e e-mail-it të ruhet dhe të përdoret për dërgimin e përditësimeve të SafetyAssist.",
    },
    email_popup_submit: {
        de: "Anmelden",
        en: "Subscribe",
        tr: "Abone ol",
        uk: "Підписатися",
        hr: "Pretplati se",
        bg: "Запиши ме",
        ru: "Подписаться",
        ro: "Abonează-te",
        pl: "Zapisz się",
        sq: "Regjistrohu",
    },
    email_popup_later: {
        de: "Vielleicht später",
        en: "Maybe later",
        tr: "Belki daha sonra",
        uk: "Можливо, пізніше",
        hr: "Možda kasnije",
        bg: "Може би по-късно",
        ru: "Возможно, позже",
        ro: "Poate mai târziu",
        pl: "Może później",
        sq: "Ndoshta më vonë",
    },
    email_popup_thanks: {
        de: "Danke! Wir melden uns sobald es Neuigkeiten gibt.",
        en: "Thanks! We'll be in touch as soon as there's news.",
        tr: "Teşekkürler! Yeni bir şey olduğunda haberdar edeceğiz.",
        uk: "Дякуємо! Ми повідомимо, щойно з'являться новини.",
        hr: "Hvala! Javit ćemo se čim bude novosti.",
        bg: "Благодарим! Ще се свържем веднага щом има новини.",
        ru: "Спасибо! Мы свяжемся, как только появятся новости.",
        ro: "Mulțumim! Vom reveni de îndată ce apar noutăți.",
        pl: "Dziękujemy! Odezwiemy się, gdy pojawią się nowości.",
        sq: "Faleminderit! Do t'ju kontaktojmë sapo të ketë lajme.",
    },
    email_popup_error_email: {
        de: "Bitte gib eine gültige E-Mail-Adresse ein.",
        en: "Please enter a valid e-mail address.",
        tr: "Lütfen geçerli bir e-posta adresi girin.",
        uk: "Будь ласка, введіть дійсну адресу електронної пошти.",
        hr: "Unesite valjanu e-mail adresu.",
        bg: "Моля, въведете валиден имейл адрес.",
        ru: "Пожалуйста, введите действительный адрес электронной почты.",
        ro: "Introduceți o adresă de e-mail validă.",
        pl: "Wprowadź prawidłowy adres e-mail.",
        sq: "Ju lutemi, vendosni një adresë e-maili të vlefshme.",
    },
    email_popup_error_consent: {
        de: "Bitte stimme der Speicherung deiner E-Mail-Adresse zu.",
        en: "Please agree to your e-mail being stored.",
        tr: "Lütfen e-postanızın saklanmasını kabul edin.",
        uk: "Будь ласка, погодьтеся на зберігання вашого e-mail.",
        hr: "Molimo pristanite na pohranu vaše e-mail adrese.",
        bg: "Моля, съгласете се с пазенето на вашия имейл адрес.",
        ru: "Пожалуйста, согласитесь на сохранение вашего e-mail.",
        ro: "Vă rugăm acceptați stocarea adresei dvs. de e-mail.",
        pl: "Zgódź się na zapisanie swojego adresu e-mail.",
        sq: "Ju lutemi, pranoni ruajtjen e adresës suaj të e-mailit.",
    },
    scancall_title: {
        de: "Wir machen die Rettungskette digital.",
        en: "We're making the rescue chain digital.",
        tr: "Kurtarma zincirini dijitalleştiriyoruz.",
        uk: "Ми переводимо ланцюг порятунку в цифровий формат.",
        hr: "Digitaliziramo lanac spašavanja.",
        bg: "Дигитализираме веригата на спасението.",
        ru: "Мы делаем цепочку спасения цифровой.",
        ro: "Digitalizăm lanțul de salvare.",
        pl: "Cyfryzujemy łańcuch ratunkowy.",
        sq: "Po e bëjmë zinxhirin e shpëtimit dixhital.",
    },
    scancall_sub: {
        de: "Ersthelfer-Aushang in Minuten – DGUV-konform & lebenslang kostenlos.",
        en: "First-responder poster in minutes – DGUV-compliant & free for life.",
        tr: "Dakikalar içinde ilk yardımcı panosu – DGUV uyumlu ve ömür boyu ücretsiz.",
        uk: "Плакат для першої допомоги за лічені хвилини – відповідає DGUV і безкоштовно назавжди.",
        hr: "Plakat za prvu pomoć u nekoliko minuta – usklađen s DGUV-om i doživotno besplatan.",
        bg: "Информационно табло за първа помощ за минути – съответства на DGUV и безплатно за цял живот.",
        ru: "Стенд первой помощи за считанные минуты – соответствует DGUV и бесплатно навсегда.",
        ro: "Afiș de prim ajutor în câteva minute – conform DGUV și gratuit pe viață.",
        pl: "Tabliczka pierwszej pomocy w kilka minut – zgodna z DGUV i bezpłatna na zawsze.",
        sq: "Postër ndihme të parë në pak minuta – në përputhje me DGUV dhe falas përgjithmonë.",
    },
    scancall_bullet_1: {
        de: "Notruf, Ersthelfer & Standort auf einen Blick",
        en: "Emergency call, first responders & location at a glance",
        tr: "Acil çağrı, ilk yardımcılar ve konum tek bakışta",
        uk: "Виклик, першочергові помічники та місце – одразу видно",
        hr: "Hitni poziv, prvi pomagači i lokacija na prvi pogled",
        bg: "Спешно повикване, първи помощници и местоположение с един поглед",
        ru: "Экстренный вызов, первые помощники и место — на одном экране",
        ro: "Apel de urgență, primii salvatori și locație dintr-o privire",
        pl: "Wezwanie, pierwsza pomoc i lokalizacja na pierwszy rzut oka",
        sq: "Thirrja e urgjencës, ndihmuesit e parë dhe vendndodhja me një shikim",
    },
    scancall_bullet_2: {
        de: "Per QR-Code direkt aufs Smartphone",
        en: "Straight to the smartphone via QR code",
        tr: "QR kodu ile doğrudan akıllı telefona",
        uk: "Через QR-код прямо на смартфон",
        hr: "Putem QR koda izravno na pametni telefon",
        bg: "Чрез QR код директно на смартфона",
        ru: "Через QR-код прямо на смартфон",
        ro: "Direct pe smartphone prin cod QR",
        pl: "Bezpośrednio na smartfon przez kod QR",
        sq: "Drejtpërdrejt në smartfon nëpërmjet kodit QR",
    },
    scancall_bullet_3: {
        de: "Aushang nach DGUV – sofort einsatzbereit",
        en: "DGUV-compliant poster – ready to use immediately",
        tr: "DGUV uyumlu pano – hemen kullanıma hazır",
        uk: "Плакат відповідно до DGUV – одразу готовий до використання",
        hr: "Plakat prema DGUV-u – odmah spreman za uporabu",
        bg: "Табло по DGUV – готово за употреба веднага",
        ru: "Плакат по DGUV — готов к использованию сразу",
        ro: "Afiș conform DGUV – gata de utilizare imediat",
        pl: "Tabliczka zgodna z DGUV – gotowa do użycia od razu",
        sq: "Posteri sipas DGUV – gati për përdorim menjëherë",
    },
    scancall_cta: {
        de: "Jetzt starten",
        en: "Start now",
        tr: "Şimdi başlat",
        uk: "Почати зараз",
        hr: "Započni sada",
        bg: "Започни сега",
        ru: "Начать сейчас",
        ro: "Începe acum",
        pl: "Zacznij teraz",
        sq: "Fillo tani",
    },
    landing_disclaimer_full: {
        de: "Die bereitgestellten Informationen dienen lediglich als Orientierungshilfe. Die Nutzung erfolgt auf eigenes Risiko und ersetzt nicht die eigene Lagebeurteilung oder professionelle Anweisungen vor Ort. Keine Gewähr für Richtigkeit. Im Zweifelsfall wählen Sie immer den {notruf}.",
        en: "The information provided is for orientation only. Use is at your own risk and does not replace your own situational judgement or professional instructions on site. No warranty for accuracy. When in doubt, always dial the {notruf}.",
        tr: "Verilen bilgiler yalnızca yönlendirme amaçlıdır. Kullanım kendi sorumluluğunuzdadır ve kendi durum değerlendirmenizi veya yerinde profesyonel talimatları yerine geçmez. Doğruluk garanti edilmez. Şüphe halinde her zaman {notruf} arayın.",
        uk: "Надана інформація є лише орієнтиром. Використання здійснюється на ваш ризик і не замінює власної оцінки ситуації або професійних інструкцій на місці. Жодної гарантії точності. У разі сумнівів завжди телефонуйте {notruf}.",
        hr: "Pružene informacije služe samo kao smjernice. Korištenje je na vlastitu odgovornost i ne zamjenjuje vlastitu procjenu situacije niti stručne upute na licu mjesta. Bez jamstva točnosti. U slučaju dvojbe uvijek nazovite {notruf}.",
        bg: "Предоставената информация служи само за ориентация. Използването е на собствен риск и не замества собствената преценка на ситуацията или професионални указания на място. Без гаранция за точност. При съмнение винаги звънете на {notruf}.",
        ru: "Предоставленная информация служит лишь ориентиром. Использование на ваш страх и риск; она не заменяет собственную оценку ситуации или профессиональные указания на месте. Точность не гарантируется. В случае сомнений всегда звоните {notruf}.",
        ro: "Informațiile furnizate sunt doar pentru orientare. Utilizarea se face pe propria răspundere și nu înlocuiește propria evaluare a situației sau instrucțiunile profesionale de la fața locului. Nicio garanție pentru corectitudine. În caz de dubiu apelați întotdeauna {notruf}.",
        pl: "Podane informacje służą wyłącznie jako wskazówka. Korzystanie odbywa się na własne ryzyko i nie zastępuje własnej oceny sytuacji ani profesjonalnych instrukcji na miejscu. Bez gwarancji poprawności. W razie wątpliwości zawsze dzwoń pod {notruf}.",
        sq: "Informacioni i dhënë shërben vetëm si orientim. Përdorimi është në përgjegjësinë tuaj dhe nuk zëvendëson vlerësimin tuaj të situatës ose udhëzimet profesionale në vend. Pa garanci për saktësinë. Në rast dyshimi telefononi gjithmonë {notruf}.",
    },
    landing_disclaimer_notruf: {
        de: "Notruf {num}",
        en: "emergency number {num}",
        tr: "acil numara {num}",
        uk: "екстрений номер {num}",
        hr: "hitni broj {num}",
        bg: "спешен номер {num}",
        ru: "экстренный номер {num}",
        ro: "numărul de urgență {num}",
        pl: "numer alarmowy {num}",
        sq: "numrin e urgjencës {num}",
    },
    footer_privacy: {
        de: "Datenschutz",
        en: "Privacy",
        tr: "Gizlilik",
        uk: "Конфіденційність",
        hr: "Privatnost",
        bg: "Поверителност",
        ru: "Конфиденциальность",
        ro: "Confidențialitate",
        pl: "Prywatność",
        sq: "Privatësia",
    },
    footer_imprint: {
        de: "Impressum",
        en: "Imprint",
        tr: "Künye",
        uk: "Вихідні дані",
        hr: "Impressum",
        bg: "Импресум",
        ru: "Импрессум",
        ro: "Impressum",
        pl: "Impressum",
        sq: "Impresum",
    },
    landing_demo_sub: {
        de: "",
        en: "",
        tr: "",
        uk: "",
        hr: "",
        bg: "",
        ru: "",
        ro: "",
        pl: "",
        sq: "",
    },
    landing_unsure_full: {
        de: "Unsicher? Im Zweifel {notruf} absetzen!",
        en: "Not sure? When in doubt, place an {notruf}!",
        tr: "Emin değil misiniz? Şüphe halinde {notruf} yapın!",
        uk: "Не впевнені? У разі сумніву зробіть {notruf}!",
        hr: "Niste sigurni? U slučaju dvojbe izvršite {notruf}!",
        bg: "Не сте сигурни? При съмнение направете {notruf}!",
        ru: "Не уверены? В случае сомнений совершите {notruf}!",
        ro: "Nu ești sigur? În caz de dubiu lansează {notruf}!",
        pl: "Niepewny? W razie wątpliwości zgłoś {notruf}!",
        sq: "Nuk jeni të sigurt? Në rast dyshimi bëni {notruf}!",
    },
    landing_unsure_notruf: {
        de: "Notruf",
        en: "emergency call",
        tr: "acil çağrı",
        uk: "екстрений виклик",
        hr: "hitni poziv",
        bg: "спешно повикване",
        ru: "экстренный вызов",
        ro: "apel de urgență",
        pl: "wezwanie alarmowe",
        sq: "thirrje urgjence",
    },

    // ---------------- SAFETY BRIEFING ----------------
    safety_title: {
        de: "Sicherheitshinweise",
        en: "Safety instructions",
        tr: "Güvenlik talimatları",
        uk: "Інструкції з безпеки",
        hr: "Sigurnosne upute",
        bg: "Инструкции за безопасност",
        ru: "Инструкции по безопасности",
        ro: "Instrucțiuni de siguranță",
        pl: "Wskazówki bezpieczeństwa",
        sq: "Udhëzime sigurie",
    },
    safety_line_1: {
        de: "Ruhe bewahren!",
        en: "Stay calm!",
        tr: "Sakin olun!",
        uk: "Зберігайте спокій!",
        hr: "Ostanite mirni!",
        bg: "Запазете спокойствие!",
        ru: "Сохраняйте спокойствие!",
        ro: "Păstrează calmul!",
        pl: "Zachowaj spokój!",
        sq: "Qetësohuni!",
    },
    safety_line_2: {
        de: "Unfallstelle sichern!",
        en: "Secure the accident site!",
        tr: "Kaza yerini güvenli hale getirin!",
        uk: "Забезпечте місце аварії!",
        hr: "Osigurajte mjesto nesreće!",
        bg: "Обезопасете мястото на произшествието!",
        ru: "Обеспечьте безопасность места происшествия!",
        ro: "Asigurați locul accidentului!",
        pl: "Zabezpiecz miejsce wypadku!",
        sq: "Siguroni vendin e aksidentit!",
    },
    safety_line_3: {
        de: "Eigene Sicherheit beachten.",
        en: "Mind your own safety.",
        tr: "Kendi güvenliğinize dikkat edin.",
        uk: "Дбайте про власну безпеку.",
        hr: "Pazite na vlastitu sigurnost.",
        bg: "Внимавайте за собствената си безопасност.",
        ru: "Следите за собственной безопасностью.",
        ro: "Aveți grijă de propria siguranță.",
        pl: "Zadbaj o własne bezpieczeństwo.",
        sq: "Kujdesuni për sigurinë tuaj.",
    },
    safety_line_4: {
        de: "Person gegebenenfalls aus dem Gefahrenbereich retten.",
        en: "If necessary, move the person out of the danger zone.",
        tr: "Gerekirse kişiyi tehlike bölgesinden çıkarın.",
        uk: "За потреби винесіть постраждалого з небезпечної зони.",
        hr: "Po potrebi izvucite osobu iz opasnog područja.",
        bg: "Ако е необходимо, изведете човека от опасната зона.",
        ru: "При необходимости выведите пострадавшего из опасной зоны.",
        ro: "Dacă este necesar, scoateți persoana din zona de pericol.",
        pl: "W razie potrzeby wyprowadź osobę ze strefy zagrożenia.",
        sq: "Nëse është e nevojshme, nxirreni personin nga zona e rrezikut.",
    },
    safety_hint: {
        de: "Du bist noch nicht allein – {num} oben jederzeit erreichbar.",
        en: "You're not alone – {num} is reachable at the top at any time.",
        tr: "Yalnız değilsin – yukarıdaki {num} her zaman ulaşılabilir.",
        uk: "Ви не самі – {num} завжди доступний угорі.",
        hr: "Niste sami – {num} je stalno dostupan na vrhu.",
        bg: "Не сте сами – {num} е достъпен отгоре по всяко време.",
        ru: "Вы не одни – {num} всегда доступен сверху.",
        ro: "Nu ești singur – {num} este disponibil sus oricând.",
        pl: "Nie jesteś sam – {num} jest dostępne u góry w każdej chwili.",
        sq: "Nuk jeni vetëm – {num} është gjithmonë i arritshëm lart.",
    },
    safety_continue: {
        de: "Weiter",
        en: "Continue",
        tr: "Devam",
        uk: "Далі",
        hr: "Dalje",
        bg: "Продължи",
        ru: "Далее",
        ro: "Continuă",
        pl: "Dalej",
        sq: "Vazhdo",
    },

    // ---------------- TRIAGE ----------------
    triage_assessment: {
        de: "Erstbeurteilung",
        en: "Initial assessment",
        tr: "İlk değerlendirme",
        uk: "Первинна оцінка",
        hr: "Početna procjena",
        bg: "Първоначална оценка",
        ru: "Первичная оценка",
        ro: "Evaluare inițială",
        pl: "Wstępna ocena",
        sq: "Vlerësimi fillestar",
    },
    triage_demo_badge: {
        de: "Demo",
        en: "Demo",
        tr: "Demo",
        uk: "Демо",
        hr: "Demo",
        bg: "Демо",
        ru: "Демо",
        ro: "Demo",
        pl: "Demo",
        sq: "Demo",
    },
    triage_check_person: {
        de: "Prüfe die Person",
        en: "Check the person",
        tr: "Kişiyi kontrol et",
        uk: "Перевірте постраждалого",
        hr: "Provjerite osobu",
        bg: "Проверете човека",
        ru: "Проверьте пострадавшего",
        ro: "Verifică persoana",
        pl: "Sprawdź osobę",
        sq: "Kontrollo personin",
    },
    triage_question_label: {
        de: "Frage",
        en: "Question",
        tr: "Soru",
        uk: "Запитання",
        hr: "Pitanje",
        bg: "Въпрос",
        ru: "Вопрос",
        ro: "Întrebare",
        pl: "Pytanie",
        sq: "Pyetje",
    },
    triage_consciousness_title: {
        de: "Bewusstsein vorhanden?",
        en: "Is the person conscious?",
        tr: "Bilinci yerinde mi?",
        uk: "Чи людина при свідомості?",
        hr: "Je li osoba pri svijesti?",
        bg: "Човекът в съзнание ли е?",
        ru: "Человек в сознании?",
        ro: "Persoana este conștientă?",
        pl: "Czy osoba jest przytomna?",
        sq: "A është personi me vetëdije?",
    },
    triage_consciousness_sub: {
        de: "Ist die Person ansprechbar",
        en: "Is the person responsive",
        tr: "Kişi cevap veriyor mu",
        uk: "Чи людина реагує",
        hr: "Reagira li osoba",
        bg: "Реагира ли човекът",
        ru: "Реагирует ли человек",
        ro: "Persoana răspunde",
        pl: "Czy osoba reaguje",
        sq: "A reagon personi",
    },
    triage_consciousness_tts: {
        de: "Ist das Bewusstsein vorhanden? Ist die Person ansprechbar?",
        en: "Is the person conscious? Are they responsive?",
        tr: "Kişinin bilinci yerinde mi? Cevap veriyor mu?",
        uk: "Чи людина при свідомості? Чи вона реагує?",
        hr: "Je li osoba pri svijesti? Reagira li?",
        bg: "Човекът в съзнание ли е? Реагира ли?",
        ru: "Человек в сознании? Он реагирует?",
        ro: "Persoana este conștientă? Răspunde?",
        pl: "Czy osoba jest przytomna? Czy reaguje?",
        sq: "A është personi me vetëdije? A reagon?",
    },
    triage_advice_conscious_title: {
        de: "Situationsgerecht helfen",
        en: "Help according to the situation",
        tr: "Duruma uygun yardım edin",
        uk: "Надайте допомогу за ситуацією",
        hr: "Pomozite prema situaciji",
        bg: "Помогнете според ситуацията",
        ru: "Помогите по ситуации",
        ro: "Ajutați conform situației",
        pl: "Pomóż zgodnie z sytuacją",
        sq: "Ndihmoni sipas situatës",
    },
    triage_advice_conscious_body: {
        de: "z. B. Wunde versorgen, Person beruhigen, vor Unterkühlung schützen.",
        en: "e.g. treat wounds, calm the person, protect from hypothermia.",
        tr: "örn. yarayı sarın, kişiyi sakinleştirin, hipotermiden koruyun.",
        uk: "напр., перев'яжіть рану, заспокойте, захистіть від переохолодження.",
        hr: "npr. previjte ranu, smirite osobu, zaštitite od pothlađivanja.",
        bg: "напр. превържете рана, успокойте човека, защитете от хипотермия.",
        ru: "напр., обработайте рану, успокойте, защитите от переохлаждения.",
        ro: "ex. tratează rana, calmează persoana, protejează de hipotermie.",
        pl: "np. opatrz ranę, uspokój osobę, chroń przed wychłodzeniem.",
        sq: "p.sh. lidhni plagën, qetësoni personin, mbroni nga hipotermia.",
    },
    triage_advice_conscious_tts: {
        de: "Hilf situationsgerecht, zum Beispiel Wunde versorgen. Drücke anschließend den großen roten Notruf-Knopf.",
        en: "Help according to the situation, for example treat wounds. Then press the big red emergency button.",
        tr: "Duruma göre yardım edin, örneğin yarayı sarın. Ardından büyük kırmızı acil durum düğmesine basın.",
        uk: "Допоможіть за ситуацією, наприклад, перев'яжіть рану. Потім натисніть велику червону кнопку екстреного виклику.",
        hr: "Pomozite prema situaciji, primjerice previjte ranu. Zatim pritisnite veliki crveni gumb za hitne pozive.",
        bg: "Помогнете според ситуацията, например превържете рана. След това натиснете големия червен бутон за спешност.",
        ru: "Помогите по ситуации, например обработайте рану. Затем нажмите большую красную кнопку экстренного вызова.",
        ro: "Ajutați în funcție de situație, de exemplu tratați rana. Apoi apăsați butonul mare roșu de urgență.",
        pl: "Pomóż zgodnie z sytuacją, na przykład opatrz ranę. Następnie naciśnij duży czerwony przycisk alarmowy.",
        sq: "Ndihmoni sipas situatës, për shembull lidhni plagën. Pastaj shtypni butonin e madh të kuq të urgjencës.",
    },
    triage_breathing_title: {
        de: "Atmung vorhanden?",
        en: "Is the person breathing?",
        tr: "Nefes alıyor mu?",
        uk: "Чи є дихання?",
        hr: "Diše li?",
        bg: "Диша ли?",
        ru: "Дышит?",
        ro: "Respiră?",
        pl: "Czy oddycha?",
        sq: "A merr frymë?",
    },
    triage_breathing_sub: {
        de: "Prüfe durch Sehen, Hören und Fühlen",
        en: "Check by looking, listening and feeling",
        tr: "Bakarak, dinleyerek ve hissederek kontrol edin",
        uk: "Перевірте, дивлячись, слухаючи та відчуваючи",
        hr: "Provjerite gledanjem, slušanjem i dodirom",
        bg: "Проверете чрез гледане, слушане и усещане",
        ru: "Проверьте, посмотрев, послушав и почувствовав",
        ro: "Verificați privind, ascultând și simțind",
        pl: "Sprawdź patrząc, słuchając i czując",
        sq: "Kontrolloni duke parë, dëgjuar dhe ndjerë",
    },
    triage_breathing_tts: {
        de: "Ist eine Atmung vorhanden? Prüfe durch Sehen, Hören und Fühlen.",
        en: "Is the person breathing? Check by looking, listening and feeling.",
        tr: "Nefes alıyor mu? Bakarak, dinleyerek ve hissederek kontrol edin.",
        uk: "Чи є дихання? Перевірте, дивлячись, слухаючи та відчуваючи.",
        hr: "Diše li? Provjerite gledanjem, slušanjem i dodirom.",
        bg: "Диша ли? Проверете чрез гледане, слушане и усещане.",
        ru: "Есть дыхание? Проверьте, посмотрев, послушав и почувствовав.",
        ro: "Respiră? Verificați privind, ascultând și simțind.",
        pl: "Czy oddycha? Sprawdź patrząc, słuchając i czując.",
        sq: "A merr frymë? Kontrolloni duke parë, dëgjuar dhe ndjerë.",
    },
    triage_advice_breathing_title: {
        de: "Stabile Seitenlage",
        en: "Recovery position",
        tr: "Derlenme pozisyonu",
        uk: "Стабільне бокове положення",
        hr: "Bočni položaj",
        bg: "Странично легнало положение",
        ru: "Устойчивое боковое положение",
        ro: "Poziția laterală de siguranță",
        pl: "Pozycja bezpieczna",
        sq: "Pozicioni anësor i sigurt",
    },
    triage_advice_breathing_body: {
        de: "Lege die Person in die stabile Seitenlage und überwache die Atmung.",
        en: "Place the person in the recovery position and monitor breathing.",
        tr: "Kişiyi derlenme pozisyonuna getirin ve nefes almasını izleyin.",
        uk: "Покладіть постраждалого у стабільне бокове положення і стежте за диханням.",
        hr: "Stavite osobu u bočni položaj i pratite disanje.",
        bg: "Поставете човека в странично легнало положение и следете дишането.",
        ru: "Уложите пострадавшего в устойчивое боковое положение и следите за дыханием.",
        ro: "Așezați persoana în poziția laterală de siguranță și monitorizați respirația.",
        pl: "Ułóż osobę w pozycji bezpiecznej i monitoruj oddech.",
        sq: "Vendoseni personin në pozicionin anësor të sigurt dhe monitoroni frymëmarrjen.",
    },
    triage_advice_breathing_tts: {
        de: "Bringe die Person in die stabile Seitenlage. Drücke anschließend den großen roten Notruf-Knopf.",
        en: "Put the person in the recovery position. Then press the big red emergency button.",
        tr: "Kişiyi derlenme pozisyonuna alın. Ardından büyük kırmızı acil durum düğmesine basın.",
        uk: "Покладіть постраждалого у стабільне бокове положення. Потім натисніть велику червону кнопку екстреного виклику.",
        hr: "Stavite osobu u bočni položaj. Zatim pritisnite veliki crveni gumb za hitne pozive.",
        bg: "Поставете човека в странично легнало положение. След това натиснете големия червен бутон за спешност.",
        ru: "Уложите пострадавшего в устойчивое боковое положение. Затем нажмите большую красную кнопку экстренного вызова.",
        ro: "Așezați persoana în poziția laterală de siguranță. Apoi apăsați butonul mare roșu de urgență.",
        pl: "Ułóż osobę w pozycji bezpiecznej. Następnie naciśnij duży czerwony przycisk alarmowy.",
        sq: "Vendoseni personin në pozicionin anësor. Pastaj shtypni butonin e madh të kuq të urgjencës.",
    },
    triage_advice_no_breath_title: {
        de: "Kein Bewusstsein und keine Atmung – Sofort Notruf",
        en: "No consciousness and no breathing – Call emergency now",
        tr: "Bilinç ve nefes yok – Hemen acil arayın",
        uk: "Немає свідомості та дихання – Терміновий виклик",
        hr: "Nema svijesti ni disanja – Odmah pozovite hitnu",
        bg: "Няма съзнание и няма дишане – Незабавно спешно повикване",
        ru: "Нет сознания и дыхания – Немедленно вызывайте скорую",
        ro: "Fără conștiență și fără respirație – Sună urgent",
        pl: "Brak przytomności i oddechu – Natychmiastowe wezwanie",
        sq: "Pa vetëdije dhe pa frymëmarrje – Thirrje e menjëhershme",
    },
    triage_advice_no_breath_body: {
        de: "Wähle sofort die {num} und beginne mit der Herzdruckmassage (30:2).",
        en: "Dial {num} immediately and start chest compressions (30:2).",
        tr: "Hemen {num}'yi arayın ve göğüs masajına başlayın (30:2).",
        uk: "Негайно наберіть {num} і почніть натискання на грудну клітку (30:2).",
        hr: "Odmah nazovite {num} i počnite s masažom srca (30:2).",
        bg: "Незабавно се обадете на {num} и започнете сърдечен масаж (30:2).",
        ru: "Немедленно наберите {num} и начните непрямой массаж сердца (30:2).",
        ro: "Sunați imediat la {num} și începeți compresiile toracice (30:2).",
        pl: "Natychmiast zadzwoń pod {num} i rozpocznij uciskanie klatki piersiowej (30:2).",
        sq: "Telefononi menjëherë {num} dhe filloni masazhin e zemrës (30:2).",
    },
    triage_advice_no_breath_tts: {
        de: "Kein Bewusstsein und keine Atmung. Drücken Sie sofort den großen roten Notruf-Knopf und beginnen Sie mit der Reanimation.",
        en: "No consciousness and no breathing. Press the big red emergency button immediately and start resuscitation.",
        tr: "Bilinç ve nefes yok. Hemen büyük kırmızı acil durum düğmesine basın ve canlandırmaya başlayın.",
        uk: "Немає свідомості та дихання. Негайно натисніть велику червону кнопку екстреного виклику і починайте реанімацію.",
        hr: "Nema svijesti ni disanja. Odmah pritisnite veliki crveni gumb za hitne pozive i započnite oživljavanje.",
        bg: "Няма съзнание и няма дишане. Незабавно натиснете големия червен бутон за спешност и започнете реанимация.",
        ru: "Нет сознания и нет дыхания. Немедленно нажмите большую красную кнопку экстренного вызова и начните реанимацию.",
        ro: "Fără conștiență și fără respirație. Apăsați imediat butonul mare roșu de urgență și începeți resuscitarea.",
        pl: "Brak przytomności i oddechu. Natychmiast naciśnij duży czerwony przycisk alarmowy i rozpocznij reanimację.",
        sq: "Pa vetëdije dhe pa frymëmarrje. Shtypni menjëherë butonin e madh të kuq të urgjencës dhe filloni ringjalljen.",
    },
    triage_yes: {
        de: "Ja",
        en: "Yes",
        tr: "Evet",
        uk: "Так",
        hr: "Da",
        bg: "Да",
        ru: "Да",
        ro: "Da",
        pl: "Tak",
        sq: "Po",
    },
    triage_no: {
        de: "Nein",
        en: "No",
        tr: "Hayır",
        uk: "Ні",
        hr: "Ne",
        bg: "Не",
        ru: "Нет",
        ro: "Nu",
        pl: "Nie",
        sq: "Jo",
    },
    triage_advice_label: {
        de: "Empfehlung",
        en: "Recommendation",
        tr: "Öneri",
        uk: "Рекомендація",
        hr: "Preporuka",
        bg: "Препоръка",
        ru: "Рекомендация",
        ro: "Recomandare",
        pl: "Zalecenie",
        sq: "Rekomandim",
    },
    triage_emergency_call: {
        de: "Notruf {num}",
        en: "Emergency {num}",
        tr: "Acil {num}",
        uk: "Екстрений {num}",
        hr: "Hitni {num}",
        bg: "Спешен {num}",
        ru: "Экстренный {num}",
        ro: "Urgență {num}",
        pl: "Alarm {num}",
        sq: "Urgjenca {num}",
    },
    triage_emergency_call_custom: {
        de: "Notruf",
        en: "Emergency",
    },
    landing_disclaimer_notruf_custom: {
        de: "hinterlegten Notruf",
        en: "on-site emergency number",
    },
    header_112_aria: {
        de: "Notruf 112 anrufen",
        en: "Call emergency number 112",
    },
    qr_landing_location_label: {
        de: "Standort",
        en: "Location",
    },
    demo_intro_eyebrow: {
        de: "Demo-Modus",
        en: "Demo mode",
    },
    demo_intro_title: {
        de: "So funktioniert Scan-and-Call",
        en: "How Scan-and-Call works",
    },
    demo_intro_body_1: {
        de: "Scan-and-Call führt dich per Sprachanleitung durch die wichtigsten Schritte der Ersten Hilfe. Du bekommst gezielte Ja/Nein-Fragen und klare Handlungsempfehlungen – ideal für den Notfall.",
        en: "Scan-and-Call uses voice guidance to walk you through the essential first-aid steps. You get targeted yes/no questions and clear action recommendations – ready for an emergency.",
    },
    demo_intro_body_2: {
        de: "In der Demo kannst du den kompletten Ablauf ausprobieren, ohne dass jemand angerufen wird. Klicke auf „Weiter“, um zu starten.",
        en: "In the demo you can try the full flow without any real call being placed. Click \"Continue\" to start.",
    },
    demo_intro_volume_tip: {
        de: "Bitte drehe die Lautstärke deines Telefons auf, damit du die Sprachanleitung gut verstehst.",
        en: "Turn up the volume on your phone so you can clearly hear the voice guidance.",
    },
    demo_intro_continue: {
        de: "Weiter zur Demo",
        en: "Continue to demo",
    },
    urgent_location_label: {
        de: "Standort",
        en: "Location",
    },
    urgent_location_tts: {
        de: "Ihr Standort ist {title}, {location}.",
        en: "Your location is {title}, {location}.",
    },
    triage_demo_alert_urgent: {
        de: "Im echten Notfall wird hier der Anruf bei der {num} ausgelöst.",
        en: "In a real emergency, this would trigger the call to {num}.",
        tr: "Gerçek bir acil durumda burada {num} araması başlatılır.",
        uk: "У справжній надзвичайній ситуації тут було б здійснено виклик {num}.",
        hr: "U stvarnoj hitnoj situaciji ovdje bi bio pokrenut poziv na {num}.",
        bg: "При истинска спешност тук би се задействало повикване на {num}.",
        ru: "В реальной экстренной ситуации здесь был бы инициирован вызов {num}.",
        ro: "Într-o urgență reală, aici ar fi declanșat apelul la {num}.",
        pl: "W prawdziwym nagłym wypadku zostałoby tu uruchomione połączenie z {num}.",
        sq: "Në një urgjencë të vërtetë, këtu do të aktivizohej thirrja për {num}.",
    },
    triage_demo_alert_generic: {
        de: "Im echten Notfall wird hier der Anruf bei der {num} ausgelöst.",
        en: "In a real emergency, this would trigger the call to {num}.",
        tr: "Gerçek bir acil durumda burada {num} araması başlatılır.",
        uk: "У справжній надзвичайній ситуації тут було б здійснено виклик {num}.",
        hr: "U stvarnoj hitnoj situaciji ovdje bi bio pokrenut poziv na {num}.",
        bg: "При истинска спешност тук би се задействало повикване на {num}.",
        ru: "В реальной экстренной ситуации здесь был бы инициирован вызов {num}.",
        ro: "Într-o urgență reală, aici ar fi declanșat apelul la {num}.",
        pl: "W prawdziwym nagłym wypadku zostałoby tu uruchomione połączenie z {num}.",
        sq: "Në një urgjencë të vërtetë, këtu do të aktivizohej thirrja për {num}.",
    },
    demo_banner_label: {
        de: "Demo-Modus aktiv",
        en: "Demo mode active",
        tr: "Demo modu etkin",
        uk: "Демо-режим активний",
        hr: "Demo način aktivan",
        bg: "Демо режим активен",
        ru: "Демо-режим активен",
        ro: "Mod demo activ",
        pl: "Tryb demo aktywny",
        sq: "Modaliteti demo aktiv",
    },
    demo_tts_prefix: {
        de: "Demo-Modus.",
        en: "Demo mode.",
        tr: "Demo modu.",
        uk: "Демо-режим.",
        hr: "Demo način.",
        bg: "Демо режим.",
        ru: "Демо-режим.",
        ro: "Mod demo.",
        pl: "Tryb demo.",
        sq: "Modaliteti demo.",
    },

    // Breadcrumb labels
    breadcrumb_conscious_no: {
        de: "Bewusstsein fehlt",
        en: "No consciousness",
        tr: "Bilinç yok",
        uk: "Немає свідомості",
        hr: "Nema svijesti",
        bg: "Няма съзнание",
        ru: "Нет сознания",
        ro: "Fără conștiență",
        pl: "Brak przytomności",
        sq: "Pa vetëdije",
    },
    breadcrumb_conscious_yes: {
        de: "Bewusstsein vorhanden",
        en: "Conscious",
        tr: "Bilinci yerinde",
        uk: "При свідомості",
        hr: "Pri svijesti",
        bg: "В съзнание",
        ru: "В сознании",
        ro: "Conștient",
        pl: "Przytomny",
        sq: "Me vetëdije",
    },
    breadcrumb_breathing_no: {
        de: "Atmung nicht vorhanden",
        en: "Not breathing",
        tr: "Nefes yok",
        uk: "Немає дихання",
        hr: "Ne diše",
        bg: "Няма дишане",
        ru: "Нет дыхания",
        ro: "Nu respiră",
        pl: "Nie oddycha",
        sq: "Pa frymëmarrje",
    },
    breadcrumb_breathing_yes: {
        de: "Atmung vorhanden",
        en: "Breathing",
        tr: "Nefes alıyor",
        uk: "Дихає",
        hr: "Diše",
        bg: "Диша",
        ru: "Дышит",
        ro: "Respiră",
        pl: "Oddycha",
        sq: "Merr frymë",
    },
    breadcrumb_called_112: {
        de: "Notruf wurde abgesetzt",
        en: "Emergency call placed",
        tr: "Acil arama yapıldı",
        uk: "Виклик здійснено",
        hr: "Poziv upućen",
        bg: "Повикването е направено",
        ru: "Вызов совершён",
        ro: "Apelul a fost efectuat",
        pl: "Wezwanie wysłane",
        sq: "Thirrja u krye",
    },

    // ---------------- CPR ----------------
    cpr_waiting_title: {
        de: "{num} wird angerufen…",
        en: "Calling {num}…",
        tr: "{num} aranıyor…",
        uk: "Виклик {num}…",
        hr: "Poziv {num}…",
        bg: "Обаждане на {num}…",
        ru: "Вызов {num}…",
        ro: "Se apelează {num}…",
        pl: "Dzwonienie {num}…",
        sq: "Po telefonon {num}…",
    },
    cpr_waiting_body: {
        de: "Führe das Gespräch mit der Leitstelle. Sobald du zur App zurückkehrst, startet die geführte Reanimation automatisch.",
        en: "Talk to the dispatcher. As soon as you return to the app, the guided resuscitation starts automatically.",
        tr: "Çağrı merkeziyle konuşun. Uygulamaya döner dönmez rehberli canlandırma otomatik başlar.",
        uk: "Спілкуйтесь з диспетчером. Щойно повернетесь до застосунку, реанімація запуститься автоматично.",
        hr: "Razgovarajte s dispečerom. Čim se vratite u aplikaciju, vođeno oživljavanje se automatski pokreće.",
        bg: "Говорете с диспечера. Щом се върнете в приложението, насоченото реанимиране започва автоматично.",
        ru: "Говорите с диспетчером. Как только вы вернётесь в приложение, руководство по реанимации начнётся автоматически.",
        ro: "Vorbiți cu dispecerul. Imediat ce vă întoarceți în aplicație, resuscitarea ghidată începe automat.",
        pl: "Rozmawiaj z dyspozytorem. Po powrocie do aplikacji prowadzona reanimacja rozpocznie się automatycznie.",
        sq: "Flisni me dispeçerin. Sapo të ktheheni në aplikacion, ringjallja e udhëhequr fillon automatikisht.",
    },
    cpr_start_now: {
        de: "Jetzt starten",
        en: "Start now",
        tr: "Şimdi başlat",
        uk: "Почати зараз",
        hr: "Započni sada",
        bg: "Започни сега",
        ru: "Начать сейчас",
        ro: "Începe acum",
        pl: "Zacznij teraz",
        sq: "Fillo tani",
    },
    cpr_intro_title: {
        de: "Wiederbelebung durchführen",
        en: "Perform resuscitation",
        tr: "Canlandırma uygulayın",
        uk: "Проведіть реанімацію",
        hr: "Izvršite oživljavanje",
        bg: "Извършете реанимация",
        ru: "Проведите реанимацию",
        ro: "Efectuează resuscitarea",
        pl: "Wykonaj resuscytację",
        sq: "Kryeni ringjalljen",
    },
    cpr_intro_body: {
        de: "Drücken Sie 2× jede Sekunde – 30×, dann 2× beatmen.",
        en: "Push twice per second – 30 times, then 2 rescue breaths.",
        tr: "Saniyede 2 kez bastırın – 30 kez, sonra 2 kez nefes verin.",
        uk: "Натискайте двічі на секунду – 30 разів, потім 2 вдихи.",
        hr: "Pritisnite dva puta u sekundi – 30 puta, zatim 2 udaha.",
        bg: "Натискайте два пъти в секунда – 30 пъти, после 2 вдишвания.",
        ru: "Нажимайте дважды в секунду – 30 раз, затем 2 вдоха.",
        ro: "Apăsați de 2 ori pe secundă – 30 de ori, apoi 2 respirații.",
        pl: "Uciskaj dwa razy na sekundę – 30 razy, potem 2 oddechy ratownicze.",
        sq: "Shtypni dy herë në sekondë – 30 herë, pastaj 2 frymëmarrje.",
    },
    cpr_intro_tts: {
        de: "Wiederbelebung durchführen. Drücken Sie 2 mal jede Sekunde und das 30 mal und dann 2 mal be-atmen.",
        en: "Perform resuscitation. Press twice per second, 30 times, then give 2 rescue breaths.",
        tr: "Canlandırma uygulayın. Saniyede iki kez bastırın, 30 kez, sonra 2 kez nefes verin.",
        uk: "Проведіть реанімацію. Натискайте двічі на секунду тридцять разів, потім два вдихи.",
        hr: "Izvršite oživljavanje. Pritisnite dva puta u sekundi trideset puta, zatim dva udaha.",
        bg: "Извършете реанимация. Натискайте два пъти в секунда тридесет пъти, после два вдиха.",
        ru: "Проведите реанимацию. Нажимайте дважды в секунду тридцать раз, затем два вдоха.",
        ro: "Efectuează resuscitarea. Apasă de două ori pe secundă, de 30 de ori, apoi 2 respirații.",
        pl: "Wykonaj resuscytację. Uciskaj dwa razy na sekundę trzydzieści razy, potem dwa oddechy ratownicze.",
        sq: "Kryeni ringjalljen. Shtypni dy herë në sekondë tridhjetë herë, pastaj dy frymëmarrje.",
    },
    cpr_start_immediately: {
        de: "Weiter",
        en: "Continue",
        tr: "Devam",
        uk: "Далі",
        hr: "Dalje",
        bg: "Продължи",
        ru: "Далее",
        ro: "Continuă",
        pl: "Dalej",
        sq: "Vazhdo",
    },
    cpr_active_title: {
        de: "Reanimation durchführen!",
        en: "Perform CPR!",
        tr: "CPR uygulayın!",
        uk: "Проводьте реанімацію!",
        hr: "Provedite oživljavanje!",
        bg: "Извършете реанимация!",
        ru: "Проводите реанимацию!",
        ro: "Efectuează CPR!",
        pl: "Wykonuj resuscytację!",
        sq: "Kryeni CPR!",
    },
    cpr_reanimation: {
        de: "Reanimation",
        en: "Resuscitation",
        tr: "Canlandırma",
        uk: "Реанімація",
        hr: "Oživljavanje",
        bg: "Реанимация",
        ru: "Реанимация",
        ro: "Resuscitare",
        pl: "Resuscytacja",
        sq: "Ringjallje",
    },
    cpr_counter_compressions: {
        de: "{n}/30 Drücken",
        en: "{n}/30 compressions",
        tr: "{n}/30 baskı",
        uk: "{n}/30 натискань",
        hr: "{n}/30 pritiska",
        bg: "{n}/30 натискания",
        ru: "{n}/30 нажатий",
        ro: "{n}/30 compresii",
        pl: "{n}/30 uciśnięć",
        sq: "{n}/30 shtypje",
    },
    cpr_counter_breaths: {
        de: "{n}/2 Beatmen",
        en: "{n}/2 breaths",
        tr: "{n}/2 nefes",
        uk: "{n}/2 вдихи",
        hr: "{n}/2 udaha",
        bg: "{n}/2 вдишвания",
        ru: "{n}/2 вдоха",
        ro: "{n}/2 respirații",
        pl: "{n}/2 oddechy",
        sq: "{n}/2 frymëmarrje",
    },
    cpr_label_compress: {
        de: "Drücken",
        en: "Push",
        tr: "Bastır",
        uk: "Натисни",
        hr: "Pritisni",
        bg: "Натискай",
        ru: "Нажимай",
        ro: "Apasă",
        pl: "Uciskaj",
        sq: "Shtyp",
    },
    cpr_label_breathe: {
        de: "Beatmen",
        en: "Breathe",
        tr: "Nefes ver",
        uk: "Вдихни",
        hr: "Udahni",
        bg: "Вдишай",
        ru: "Дыши",
        ro: "Respiră",
        pl: "Oddychaj",
        sq: "Frymë",
    },
    cpr_bpm_line: {
        de: "110 BPM · 545 ms pro Drücken",
        en: "110 BPM · 545 ms per compression",
        tr: "110 BPM · 545 ms baskı başına",
        uk: "110 уд./хв · 545 мс на натискання",
        hr: "110 BPM · 545 ms po pritisku",
        bg: "110 BPM · 545 мс на натискане",
        ru: "110 уд/мин · 545 мс на нажатие",
        ro: "110 BPM · 545 ms pe compresie",
        pl: "110 BPM · 545 ms na uciśnięcie",
        sq: "110 BPM · 545 ms për shtypje",
    },
    cpr_timer_suffix: {
        de: "30× drücken · 2× beatmen",
        en: "30× push · 2× breaths",
        tr: "30× baskı · 2× nefes",
        uk: "30× натискань · 2× вдихи",
        hr: "30× pritisak · 2× udaha",
        bg: "30× натискане · 2× вдишвания",
        ru: "30× нажатий · 2× вдоха",
        ro: "30× apăsare · 2× respirații",
        pl: "30× ucisk · 2× oddechy",
        sq: "30× shtypje · 2× frymëmarrje",
    },
    cpr_continue_hint: {
        de: "Weitermachen bis Hilfe eintrifft.",
        en: "Continue until help arrives.",
        tr: "Yardım gelene kadar devam edin.",
        uk: "Продовжуйте, доки прибуде допомога.",
        hr: "Nastavite dok pomoć ne stigne.",
        bg: "Продължавайте, докато пристигне помощ.",
        ru: "Продолжайте, пока не прибудет помощь.",
        ro: "Continuați până sosește ajutorul.",
        pl: "Kontynuuj, aż przybędzie pomoc.",
        sq: "Vazhdoni derisa të vijë ndihma.",
    },
    cpr_handover_tts: {
        de: "Rufen Sie nach Ablöse!",
        en: "Call for a replacement!",
        tr: "Değişim için yardım isteyin!",
        uk: "Викличте заміну!",
        hr: "Pozovite zamjenu!",
        bg: "Повикайте смяна!",
        ru: "Позовите на замену!",
        ro: "Cere înlocuire!",
        pl: "Wezwij zmiennika!",
        sq: "Thirrni për ndërrim!",
    },
    cpr_breath_tts: {
        de: "Be-atmen",
        en: "Breathe",
        tr: "Nefes ver",
        uk: "Вдихни",
        hr: "Udahni",
        bg: "Вдишай",
        ru: "Дыши",
        ro: "Respiră",
        pl: "Oddychaj",
        sq: "Frymë",
    },
    cpr_show_aed: {
        de: "Defibrillator in der Nähe",
        en: "Defibrillator nearby",
        tr: "Yakındaki defibrilatör",
        uk: "Дефібрилятор поруч",
        hr: "Defibrilator u blizini",
        bg: "Дефибрилатор наблизо",
        ru: "Дефибриллятор рядом",
        ro: "Defibrilator în apropiere",
        pl: "Defibrylator w pobliżu",
        sq: "Defibrilator afër",
    },

    // ---------------- AED ----------------
    aed_back: {
        de: "Zurück",
        en: "Back",
        tr: "Geri",
        uk: "Назад",
        hr: "Natrag",
        bg: "Назад",
        ru: "Назад",
        ro: "Înapoi",
        pl: "Wstecz",
        sq: "Kthehu",
    },
    aed_section_label: {
        de: "Defibrillator-Suche",
        en: "Defibrillator search",
        tr: "Defibrilatör arama",
        uk: "Пошук дефібрилятора",
        hr: "Pretraga defibrilatora",
        bg: "Търсене на дефибрилатор",
        ru: "Поиск дефибриллятора",
        ro: "Căutare defibrilator",
        pl: "Wyszukiwanie defibrylatora",
        sq: "Kërkim defibrilatori",
    },
    aed_confirm_title: {
        de: "Befinden Sie sich noch an diesem Standort?",
        en: "Are you still at this location?",
        tr: "Hâlâ bu konumda mısınız?",
        uk: "Ви все ще за цією адресою?",
        hr: "Jeste li još uvijek na ovoj lokaciji?",
        bg: "Все още ли сте на това местоположение?",
        ru: "Вы всё ещё в этом месте?",
        ro: "Mai sunteți la această locație?",
        pl: "Czy nadal jesteś w tym miejscu?",
        sq: "A jeni ende në këtë vendndodhje?",
    },
    aed_confirm_yes: {
        de: "Ja, weiter",
        en: "Yes, continue",
        tr: "Evet, devam",
        uk: "Так, далі",
        hr: "Da, dalje",
        bg: "Да, продължи",
        ru: "Да, продолжить",
        ro: "Da, continuă",
        pl: "Tak, kontynuuj",
        sq: "Po, vazhdo",
    },
    aed_confirm_no: {
        de: "Nein, neuer Standort",
        en: "No, new location",
        tr: "Hayır, yeni konum",
        uk: "Ні, нова адреса",
        hr: "Ne, nova lokacija",
        bg: "Не, ново място",
        ru: "Нет, новое место",
        ro: "Nu, locație nouă",
        pl: "Nie, nowa lokalizacja",
        sq: "Jo, vendndodhje e re",
    },
    aed_detecting_title: {
        de: "Standort wird erfasst…",
        en: "Detecting location…",
        tr: "Konum tespit ediliyor…",
        uk: "Визначається місцезнаходження…",
        hr: "Utvrđivanje lokacije…",
        bg: "Откриване на местоположението…",
        ru: "Определение местоположения…",
        ro: "Se detectează locația…",
        pl: "Wykrywanie lokalizacji…",
        sq: "Po zbulohet vendndodhja…",
    },
    aed_detecting_sub: {
        de: "Bitte erlaube die Standortfreigabe im Browser-Dialog.",
        en: "Please allow location access in the browser prompt.",
        tr: "Lütfen tarayıcı iletişim kutusunda konum erişimine izin verin.",
        uk: "Дозвольте доступ до геолокації у вікні браузера.",
        hr: "Dopustite pristup lokaciji u dijalogu preglednika.",
        bg: "Моля, разрешете достъп до местоположението в диалога на браузъра.",
        ru: "Разрешите доступ к местоположению в диалоге браузера.",
        ro: "Permiteți accesul la locație în dialogul browserului.",
        pl: "Zezwól na dostęp do lokalizacji w oknie przeglądarki.",
        sq: "Lejoni aksesin në vendndodhje në dialogun e shfletuesit.",
    },
    aed_detecting_hint: {
        de: "Falls der Dialog nicht erscheint oder du abgelehnt hast, kannst du auch eine PLZ eingeben.",
        en: "If the prompt doesn't show or you declined, you can also enter a postcode.",
        tr: "İletişim kutusu görünmezse veya reddettiyseniz posta kodunu da girebilirsiniz.",
        uk: "Якщо діалог не з'явився або ви відмовили, можна ввести поштовий індекс.",
        hr: "Ako se dijalog ne pojavi ili ste odbili, možete unijeti i poštanski broj.",
        bg: "Ако диалогът не се появи или сте отказали, можете да въведете пощенски код.",
        ru: "Если диалог не появился или вы отказали, можно ввести почтовый индекс.",
        ro: "Dacă dialogul nu apare sau l-ai refuzat, poți introduce un cod poștal.",
        pl: "Jeśli okno nie pojawiło się lub odmówiłeś, możesz podać kod pocztowy.",
        sq: "Nëse dialogu nuk shfaqet ose e refuzuat, mund të futni edhe kodin postar.",
    },
    aed_enter_plz: {
        de: "PLZ eingeben",
        en: "Enter postcode",
        tr: "Posta kodu girin",
        uk: "Введіть індекс",
        hr: "Unesite poštanski broj",
        bg: "Въведете пощенски код",
        ru: "Введите индекс",
        ro: "Introduceți codul poștal",
        pl: "Wpisz kod pocztowy",
        sq: "Futni kodin postar",
    },
    aed_manual_title: {
        de: "PLZ eingeben",
        en: "Enter postcode",
        tr: "Posta kodu girin",
        uk: "Введіть поштовий індекс",
        hr: "Unesite poštanski broj",
        bg: "Въведете пощенски код",
        ru: "Введите почтовый индекс",
        ro: "Introduceți codul poștal",
        pl: "Wpisz kod pocztowy",
        sq: "Futni kodin postar",
    },
    aed_manual_sub: {
        de: "Standortfreigabe nicht aktiv – gib deine PLZ ein, um die Defibrillatoren in der Nähe zu finden.",
        en: "Location access is not active – enter your postcode to find nearby defibrillators.",
        tr: "Konum erişimi aktif değil – yakındaki defibrilatörleri bulmak için posta kodunu girin.",
        uk: "Геолокація вимкнена – введіть індекс, щоб знайти дефібрилятори поруч.",
        hr: "Pristup lokaciji nije aktivan – unesite poštanski broj za pretragu defibrilatora u blizini.",
        bg: "Достъпът до местоположение не е активен – въведете пощенски код, за да намерите дефибрилатори наблизо.",
        ru: "Доступ к геолокации отключён – введите индекс, чтобы найти дефибрилляторы поблизости.",
        ro: "Accesul la locație nu este activ – introduceți codul poștal pentru a găsi defibrilatoare în apropiere.",
        pl: "Dostęp do lokalizacji nie jest aktywny – wpisz kod pocztowy, aby znaleźć defibrylatory w pobliżu.",
        sq: "Aksesi në vendndodhje nuk është aktiv – futni kodin postar për të gjetur defibrilatorë afër.",
    },
    aed_manual_hint: {
        de: "Tipp: Du kannst auch erneut den Standort freigeben, wenn dein Browser dies unterstützt.",
        en: "Tip: you can also enable location access again if your browser supports it.",
        tr: "İpucu: Tarayıcınız destekliyorsa konum erişimini tekrar etkinleştirebilirsiniz.",
        uk: "Порада: якщо браузер підтримує, можна знову увімкнути геолокацію.",
        hr: "Savjet: možete ponovno omogućiti pristup lokaciji ako to vaš preglednik podržava.",
        bg: "Съвет: можете отново да разрешите достъп до местоположението, ако браузърът поддържа.",
        ru: "Совет: если браузер поддерживает, можно снова включить геолокацию.",
        ro: "Sfat: poți reactiva accesul la locație dacă browserul permite.",
        pl: "Wskazówka: możesz też ponownie udostępnić lokalizację, jeśli przeglądarka to obsługuje.",
        sq: "Këshillë: mund të aktivizoni përsëri aksesin në vendndodhje nëse shfletuesi e mbështet.",
    },
    aed_find_button: {
        de: "Defibrillatoren finden",
        en: "Find defibrillators",
        tr: "Defibrilatörleri bul",
        uk: "Знайти дефібрилятори",
        hr: "Pronađi defibrilatore",
        bg: "Намери дефибрилатори",
        ru: "Найти дефибрилляторы",
        ro: "Găsește defibrilatoare",
        pl: "Znajdź defibrylatory",
        sq: "Gjej defibrilatorë",
    },
    aed_retry_gps: {
        de: "Standort erneut",
        en: "Retry location",
        tr: "Konumu yeniden dene",
        uk: "Повторити геолокацію",
        hr: "Ponovi lokaciju",
        bg: "Опитай пак местоположение",
        ru: "Повторить геолокацию",
        ro: "Reîncearcă locația",
        pl: "Spróbuj ponownie",
        sq: "Riprovo vendndodhjen",
    },
    aed_toast_invalid_plz: {
        de: "Bitte eine gültige 5-stellige PLZ eingeben.",
        en: "Please enter a valid 5-digit postcode.",
        tr: "Lütfen geçerli bir 5 haneli posta kodu girin.",
        uk: "Введіть дійсний 5-значний індекс.",
        hr: "Unesite valjan 5-znamenkasti poštanski broj.",
        bg: "Моля, въведете валиден 5-цифрен пощенски код.",
        ru: "Пожалуйста, введите действительный 5-значный индекс.",
        ro: "Introduceți un cod poștal valid de 5 cifre.",
        pl: "Wpisz poprawny 5-cyfrowy kod pocztowy.",
        sq: "Ju lutem, futni një kod postar të vlefshëm me 5 shifra.",
    },
    aed_toast_plz_failed: {
        de: "PLZ konnte nicht aufgelöst werden.",
        en: "Postcode could not be resolved.",
        tr: "Posta kodu çözümlenemedi.",
        uk: "Не вдалося розпізнати поштовий індекс.",
        hr: "Poštanski broj nije mogao biti obrađen.",
        bg: "Пощенският код не може да бъде разпознат.",
        ru: "Не удалось распознать индекс.",
        ro: "Codul poștal nu a putut fi rezolvat.",
        pl: "Nie udało się zidentyfikować kodu pocztowego.",
        sq: "Kodi postar nuk mund të zgjidhet.",
    },
    aed_toast_no_geo: {
        de: "Geolocation wird vom Browser nicht unterstützt.",
        en: "Geolocation is not supported by the browser.",
        tr: "Konum tarayıcı tarafından desteklenmiyor.",
        uk: "Браузер не підтримує геолокацію.",
        hr: "Geolokaciju preglednik ne podržava.",
        bg: "Геолокацията не се поддържа от браузъра.",
        ru: "Браузер не поддерживает геолокацию.",
        ro: "Geolocația nu este suportată de browser.",
        pl: "Przeglądarka nie obsługuje geolokalizacji.",
        sq: "Shfletuesi nuk e mbështet gjeolokacionin.",
    },
    aed_toast_geo_denied: {
        de: "Standortfreigabe verweigert oder fehlgeschlagen.",
        en: "Location permission denied or failed.",
        tr: "Konum izni reddedildi veya başarısız oldu.",
        uk: "Доступ до геолокації заборонено або сталася помилка.",
        hr: "Pristup lokaciji odbijen ili neuspješan.",
        bg: "Достъпът до местоположение е отказан или неуспешен.",
        ru: "Доступ к геолокации отклонён или не удался.",
        ro: "Permisiunea locației a fost refuzată sau a eșuat.",
        pl: "Odmowa dostępu do lokalizacji lub błąd.",
        sq: "Leja e vendndodhjes u refuzua ose dështoi.",
    },
    aed_placeholder_plz: {
        de: "z. B. 10115",
        en: "e.g. 10115",
        tr: "örn. 10115",
        uk: "напр., 10115",
        hr: "npr. 10115",
        bg: "напр. 10115",
        ru: "напр. 10115",
        ro: "ex. 10115",
        pl: "np. 10115",
        sq: "p.sh. 10115",
    },

    // ---------------- DEFIBRILLATOR MAP ----------------
    map_section_label: {
        de: "Defibrillatoren in der Nähe",
        en: "Defibrillators nearby",
        tr: "Yakındaki defibrilatörler",
        uk: "Дефібрилятори поруч",
        hr: "Defibrilatori u blizini",
        bg: "Дефибрилатори наблизо",
        ru: "Дефибрилляторы рядом",
        ro: "Defibrilatoare în apropiere",
        pl: "Defibrylatory w pobliżu",
        sq: "Defibrilatorë afër",
    },
    map_count: {
        de: "{n} Defibrillator-Standorte",
        en: "{n} defibrillator locations",
        tr: "{n} defibrilatör konumu",
        uk: "{n} локацій дефібриляторів",
        hr: "{n} lokacija defibrilatora",
        bg: "{n} места с дефибрилатори",
        ru: "{n} мест с дефибрилляторами",
        ro: "{n} locații defibrilator",
        pl: "{n} miejsc z defibrylatorami",
        sq: "{n} vendndodhje defibrilatorësh",
    },
    map_your_location: {
        de: "Dein Standort",
        en: "Your location",
        tr: "Konumunuz",
        uk: "Ваше місце",
        hr: "Vaša lokacija",
        bg: "Вашето местоположение",
        ru: "Ваше местоположение",
        ro: "Locația ta",
        pl: "Twoja lokalizacja",
        sq: "Vendndodhja juaj",
    },
    map_aed_label: {
        de: "Defibrillator",
        en: "Defibrillator",
        tr: "Defibrilatör",
        uk: "Дефібрилятор",
        hr: "Defibrilator",
        bg: "Дефибрилатор",
        ru: "Дефибриллятор",
        ro: "Defibrilator",
        pl: "Defibrylator",
        sq: "Defibrilator",
    },
    map_you_are_here: {
        de: "Du bist hier",
        en: "You are here",
        tr: "Buradasınız",
        uk: "Ви тут",
        hr: "Ovdje ste",
        bg: "Вие сте тук",
        ru: "Вы здесь",
        ro: "Ești aici",
        pl: "Tu jesteś",
        sq: "Ju jeni këtu",
    },
    map_operator: {
        de: "Betreiber",
        en: "Operator",
        tr: "Operatör",
        uk: "Оператор",
        hr: "Operater",
        bg: "Оператор",
        ru: "Оператор",
        ro: "Operator",
        pl: "Operator",
        sq: "Operatori",
    },
    map_indoor: {
        de: "Im Gebäude",
        en: "Indoor",
        tr: "Bina içi",
        uk: "У приміщенні",
        hr: "Unutra",
        bg: "В сграда",
        ru: "В помещении",
        ro: "În interior",
        pl: "W budynku",
        sq: "Brenda",
    },
    map_outdoor: {
        de: "Außenstandort",
        en: "Outdoor",
        tr: "Dış alan",
        uk: "На відкритому повітрі",
        hr: "Vanjski",
        bg: "На открито",
        ru: "Снаружи",
        ro: "În aer liber",
        pl: "Na zewnątrz",
        sq: "Jashtë",
    },
    map_opening_hours: {
        de: "Öffnungszeiten",
        en: "Opening hours",
        tr: "Çalışma saatleri",
        uk: "Години роботи",
        hr: "Radno vrijeme",
        bg: "Работно време",
        ru: "Часы работы",
        ro: "Program",
        pl: "Godziny otwarcia",
        sq: "Orari",
    },

    // ---------------- COMMON ----------------
    tts_activate: {
        de: "Sprachausgabe aktivieren",
        en: "Enable voice output",
        tr: "Sesli çıktıyı etkinleştir",
        uk: "Увімкнути голос",
        hr: "Uključi govor",
        bg: "Включи гласа",
        ru: "Включить голос",
        ro: "Activează vocea",
        pl: "Włącz głos",
        sq: "Aktivizo zërin",
    },
    tts_mute: {
        de: "Sprachausgabe stummschalten",
        en: "Mute voice output",
        tr: "Sesli çıktıyı sustur",
        uk: "Вимкнути голос",
        hr: "Isključi govor",
        bg: "Заглуши гласа",
        ru: "Выключить голос",
        ro: "Dezactivează vocea",
        pl: "Wycisz głos",
        sq: "Hesht zërin",
    },
    reset_link: {
        de: "Wiederholen – Zurück an den Anfang",
        en: "Restart – back to the beginning",
        tr: "Baştan başla – başa dön",
        uk: "Повторити – повернутися на початок",
        hr: "Ponovi – natrag na početak",
        bg: "Повтори – обратно в началото",
        ru: "Повторить – вернуться к началу",
        ro: "Reia – înapoi la început",
        pl: "Powtórz – wróć na początek",
        sq: "Rifillo – kthehu në fillim",
    },
    footer_powered_by: {
        de: "Powered by SafetyAssist",
        en: "Powered by SafetyAssist",
        tr: "SafetyAssist tarafından sağlanır",
        uk: "Працює на SafetyAssist",
        hr: "Pokreće SafetyAssist",
        bg: "Задвижвано от SafetyAssist",
        ru: "Работает на SafetyAssist",
        ro: "Realizat de SafetyAssist",
        pl: "Napędzane przez SafetyAssist",
        sq: "Mundësuar nga SafetyAssist",
    },
    // QR personalisation (only shown when the URL contains ?id=...)
    qr_personalize_title: {
        de: "QR-Code personalisieren",
        en: "Personalise your QR code",
    },
    qr_personalize_body: {
        de: "Hinterlege einen Standortnamen und den Standort des nächsten Defibrillators. Beim nächsten Aufruf dieser QR-Adresse werden diese Infos direkt angezeigt – Standort gross auf der Startseite, Defi-Standort während der Reanimation.",
        en: "Set a location name and the nearest defibrillator location. On the next visit with this QR code, both are shown right away – location prominently on the home page, defibrillator hint during CPR.",
    },
    qr_field_location_name: {
        de: "Standortname",
        en: "Location name",
    },
    qr_field_location_placeholder: {
        de: "z. B. Werkstatt 3 – Empfang",
        en: "e.g. Workshop 3 – Reception",
    },
    qr_field_defib_location: {
        de: "Nächster Defibrillator",
        en: "Nearest defibrillator",
    },
    qr_field_defib_placeholder: {
        de: "z. B. Kantine, neben Eingang B",
        en: "e.g. Canteen, next to entrance B",
    },
    qr_personalize_submit: {
        de: "Speichern und anzeigen",
        en: "Save and show",
    },
    qr_personalize_skip: {
        de: "Überspringen",
        en: "Skip",
    },
    qr_error_location_required: {
        de: "Bitte gib einen Standortnamen ein.",
        en: "Please enter a location name.",
    },
    qr_error_save_failed: {
        de: "Speichern fehlgeschlagen – bitte erneut versuchen.",
        en: "Saving failed – please try again.",
    },
    qr_landing_at_label: {
        de: "An diesem Standort",
        en: "At this location",
    },
    qr_cpr_defib_label: {
        de: "Nächster Defibrillator",
        en: "Nearest defibrillator",
    },
    // Sticker request (shown when no qrId in URL)
    sticker_offer_title: {
        de: "Personalisierte Version für Ihren Standort",
        en: "Personalised version for your location",
    },
    sticker_offer_body: {
        de: "Sie können eine personalisierte Version für Ihren Standort erstellen – fordern Sie dazu einfach die personalisierten Scan&Call-Aufkleber (kostenlos) an.",
        en: "You can create a personalised version for your location – just request our personalised Scan&Call stickers (free of charge).",
    },
    sticker_offer_cta: {
        de: "Aufkleber kostenlos anfordern",
        en: "Request stickers free of charge",
    },
    sticker_offer_skip: {
        de: "Vielleicht später",
        en: "Maybe later",
    },
    sticker_form_title: {
        de: "Firmenadresse für den Versand",
        en: "Company address for shipping",
    },
    sticker_form_body: {
        de: "Wir senden Ihnen die Scan&Call-Aufkleber kostenfrei zu. Bitte geben Sie die Anschrift Ihrer Firma an.",
        en: "We will send the Scan&Call stickers to you free of charge. Please provide your company address.",
    },
    sticker_field_company: {
        de: "Firma *",
        en: "Company *",
    },
    sticker_field_contact: {
        de: "Ansprechpartner",
        en: "Contact person",
    },
    sticker_field_street: {
        de: "Straße & Hausnummer *",
        en: "Street & number *",
    },
    sticker_field_zip: {
        de: "PLZ *",
        en: "ZIP *",
    },
    sticker_field_city: {
        de: "Stadt *",
        en: "City *",
    },
    sticker_field_country: {
        de: "Land",
        en: "Country",
    },
    sticker_field_phone: {
        de: "Telefon",
        en: "Phone",
    },
    sticker_field_quantity: {
        de: "Anzahl",
        en: "Quantity",
    },
    sticker_field_notes: {
        de: "Anmerkungen (optional)",
        en: "Notes (optional)",
    },
    sticker_submit: {
        de: "Jetzt kostenlos anfordern",
        en: "Request now",
    },
    sticker_error_required: {
        de: "Bitte Firma, Straße, PLZ und Stadt ausfüllen.",
        en: "Please fill in company, street, ZIP and city.",
    },
    sticker_error_save_failed: {
        de: "Speichern fehlgeschlagen – bitte erneut versuchen.",
        en: "Saving failed – please try again.",
    },
    // Landing promo card – pushes /landing marketing page
    promo_card_eyebrow: {
        de: "Mehr entdecken",
        en: "Discover more",
    },
    promo_card_title: {
        de: "Scan&Call – die digitale Rettungskette",
        en: "Scan&Call – the digital rescue chain",
    },
    promo_card_body: {
        de: "DGUV-konformer Erste-Hilfe-Aushang, ortspersonalisierte Notruf-Hilfe per QR und vieles mehr. Sehen Sie alle Features und Anwendungsfälle auf einen Blick.",
        en: "DGUV-compliant first-aid poster, location-personalised emergency assistance via QR and much more. See all features and use cases at a glance.",
    },
    promo_card_cta: {
        de: "Alle Features ansehen",
        en: "See all features",
    },
    // Email popup feature teasers
    email_popup_eyebrow: {
        de: "SafetyAssist kann noch mehr",
        en: "SafetyAssist can do more",
    },
    email_popup_feature_1: {
        de: "Personalisierte QR-Codes für Ihren Standort & Defi-Hinweise",
        en: "Personalised QR codes for your location & defibrillator hints",
    },
    email_popup_feature_2: {
        de: "Sprachgeführte Reanimation in 28 Sprachen",
        en: "Voice-guided CPR in 28 languages",
    },
    email_popup_feature_3: {
        de: "DGUV-konformer Erste-Hilfe-Aushang – kostenlos für Firmen",
        en: "DGUV-compliant first-aid poster – free for companies",
    },
    // Marketing landing page (/landing)
    header_demo: {
        de: "Ich will wissen wie es funktioniert – Demo starten",
        en: "I want to see how it works – start demo",
    },
    hero_eyebrow: {
        de: "Erste Hilfe digital – DGUV-konform",
        en: "First aid, digital – DGUV-compliant",
    },
    hero_title: {
        de: "Notfall? Sofort der richtige nächste Schritt.",
        en: "Emergency? The right next step, instantly.",
    },
    hero_subtitle: {
        de: "Sprachgeführte Reanimation, ortspersonalisierte QR-Codes, Defibrillator-Karte und Notfall-Aushänge – alles in einer App, in 28 Sprachen.",
        en: "Voice-guided CPR, location-personalised QR codes, defibrillator map and emergency posters – all in one app, in 28 languages.",
    },
    hero_cta_demo: {
        de: "Ich will wissen wie es funktioniert – Demo starten",
        en: "I want to see how it works – start demo",
    },
    hero_cta_app: {
        de: "Ich habe einen echten Notfall, bitte hilf mir!",
        en: "I have a real emergency, please help me!",
    },
    hero_poster_alt: {
        de: "DGUV Erste-Hilfe Aushang mit Scan & Call QR-Code",
        en: "DGUV first-aid poster with Scan & Call QR code",
    },
    features_title: {
        de: "Alles für die Erste-Hilfe-Kette in einer App",
        en: "Everything for the first-aid chain in one app",
    },
    features_subtitle: {
        de: "Von der ersten Sekunde bis zum Eintreffen des Rettungsdienstes – SafetyAssist führt Sie durch jeden Schritt.",
        en: "From the first second until the rescue team arrives – SafetyAssist guides you through every step.",
    },
    feature_triage_title: { de: "Triage-Assistent", en: "Triage assistant" },
    feature_triage_body: {
        de: "Bewusstsein und Atmung prüfen – einfache Ja/Nein-Fragen führen zum richtigen Vorgehen.",
        en: "Check consciousness and breathing – simple yes/no questions lead to the right course of action.",
    },
    feature_voice_title: { de: "Sprachausgabe (TTS)", en: "Voice output (TTS)" },
    feature_voice_body: {
        de: "Klare Anweisungen werden vorgelesen und wiederholt – auch wenn die Hände beschäftigt sind.",
        en: "Clear instructions are read aloud and repeated – even while your hands are busy.",
    },
    feature_lang_title: { de: "28 Sprachen", en: "28 languages" },
    feature_lang_body: {
        de: "Von Albanisch bis Ungarisch – inkl. automatischer Erkennung schweizer (144) Notrufnummern.",
        en: "From Albanian to Hungarian – including auto-detection of Swiss (144) emergency numbers.",
    },
    feature_cpr_title: { de: "Reanimations-Metronom", en: "CPR metronome" },
    feature_cpr_body: {
        de: "110 BPM präziser Takt mit visuellen und akustischen Cues für perfekte Herzdruckmassage.",
        en: "110 BPM precise rhythm with visual and acoustic cues for perfect chest compressions.",
    },
    feature_qr_title: { de: "Personalisierte QR-Codes", en: "Personalised QR codes" },
    feature_qr_body: {
        de: "Standortname und Defi-Position direkt auf der Hilfe-Seite – ortsbezogen und sofort sichtbar.",
        en: "Location name and defibrillator position right on the help page – contextual and instantly visible.",
    },
    feature_mobile_title: { de: "Mobile-optimiert", en: "Mobile-optimised" },
    feature_mobile_body: {
        de: "Riesige Buttons, Wake-Lock gegen Bildschirm-Schlaf und voller Offline-Fallback.",
        en: "Huge buttons, wake-lock to prevent screen sleep and full offline fallback.",
    },
    defimap_title: {
        de: "Defibrillator in der Nähe finden",
        en: "Find a defibrillator nearby",
    },
    defimap_subtitle: {
        de: "Über 70.000 öffentliche AEDs aus OpenStreetMap – mit einem Klick in Ihrer Umgebung.",
        en: "Over 70,000 public AEDs from OpenStreetMap – one click away from you.",
    },
    defimap_cta_title: { de: "Standort freigeben", en: "Share location" },
    defimap_cta_body: {
        de: "Wir nutzen Ihren Standort nur in Ihrem Browser, um Defibrillatoren in der Nähe zu finden.",
        en: "We use your location only inside your browser to find nearby defibrillators.",
    },
    defimap_locate: { de: "Defibrillatoren finden", en: "Find defibrillators" },
    defimap_locating: { de: "Suche Standort…", en: "Locating…" },
    landing_no_geo: {
        de: "Ihr Browser unterstützt keine Standortermittlung.",
        en: "Your browser does not support geolocation.",
    },
    landing_geo_denied: {
        de: "Standort konnte nicht ermittelt werden.",
        en: "Location could not be determined.",
    },
    footer_tagline: {
        de: "Die digitale Rettungskette – damit jeder im Notfall sofort weiß, was zu tun ist.",
        en: "The digital rescue chain – so everyone knows what to do in an emergency, instantly.",
    },
    footer_to_app: { de: "Zur Notfall-App", en: "To the emergency app" },

    // QR Create Modal
    qr_create_header: { de: "Personalisierung", en: "Personalise" },
    qr_create_title: {
        de: "Diesen QR-Code einrichten",
        en: "Set up this QR code",
    },
    qr_create_body: {
        de: "Dieser QR-Code ist noch nicht personalisiert. Geben Sie eine Bezeichnung an, damit Helfer auf der Startseite direkt sehen, wo sie sind. Ein Standort kann optional ergänzt werden – er wird während der Reanimation sichtbar.",
        en: "This QR code is not yet personalised. Add a label so first responders see exactly where they are. A location is optional – it shows up during CPR.",
    },
    qr_create_field_title: { de: "Bezeichnung", en: "Label" },
    qr_create_field_title_placeholder: {
        de: "z. B. Werkstatt 3 – Empfang",
        en: "e.g. Workshop 3 – Reception",
    },
    qr_create_field_location: { de: "Standort", en: "Location" },
    qr_create_field_location_placeholder: {
        de: "z. B. Defi: Kantine neben Eingang B",
        en: "e.g. AED: canteen next to entrance B",
    },
    qr_create_field_optional: { de: "optional", en: "optional" },
    qr_create_submit: {
        de: "Speichern und anzeigen",
        en: "Save and show",
    },
    qr_create_error_required: {
        de: "Bitte eine Bezeichnung angeben.",
        en: "Please provide a label.",
    },
    qr_create_error_failed: {
        de: "Speichern fehlgeschlagen.",
        en: "Saving failed.",
    },
    qr_create_gps_button: {
        de: "Aktuellen Standort verwenden",
        en: "Use current location",
    },
    qr_create_gps_used: {
        de: "GPS-Standort übernommen",
        en: "GPS location applied",
    },
    qr_create_gps_denied: {
        de: "Standort-Zugriff abgelehnt – bitte in den Browser-Einstellungen erlauben.",
        en: "Location permission denied – please allow it in your browser settings.",
    },
    qr_create_gps_error: {
        de: "Standort konnte nicht ermittelt werden.",
        en: "Could not determine your location.",
    },
    qr_create_gps_unsupported: {
        de: "GPS wird von diesem Gerät nicht unterstützt.",
        en: "GPS is not supported on this device.",
    },
    qr_create_field_aed: {
        de: "Defibrillator (AED) in der Nähe",
        en: "Defibrillator (AED) nearby",
    },
    qr_create_field_aed_placeholder: {
        de: "z. B. Empfangsbereich, neben Aufzug 2",
        en: "e.g. reception area, next to elevator 2",
    },
    qr_create_field_aed_help: {
        de: "Wird Helfern unter dem Notruf-Button angezeigt, sobald eine Reanimation erforderlich ist.",
        en: "Shown to helpers below the emergency call button when CPR is needed.",
    },
    triage_aed_label: {
        de: "Nächster Defibrillator",
        en: "Nearest defibrillator",
    },
    emergency_hint_ch: {
        de: "In der Schweiz auch als 144 bekannt – 112 funktioniert genauso.",
        en: "Also known as 144 in Switzerland – 112 works the same way.",
    },
    emergency_hint_uk: {
        de: "In Großbritannien auch als 999 bekannt – 112 funktioniert genauso.",
        en: "Also known as 999 in the UK – 112 works the same way.",
    },
    resolver_warning_text: {
        de: "Branding-Info nicht verfügbar – Notfall-Funktionen sind uneingeschränkt aktiv.",
        en: "Branding info unavailable – emergency functions remain fully active.",
    },
    powered_by_line1: {
        de: "Powered by SafetyAssist",
        en: "Powered by SafetyAssist",
    },
    powered_by_line2: {
        de: "SafetyAssist ist ein Produkt der Makrolog AG",
        en: "SafetyAssist is a product of Makrolog AG",
    },

    // Subtle demo link below the emergency button on the landing page
    landing_demo_link: {
        de: "Ich habe keinen Notfall – Demo starten",
        en: "No emergency – start demo",
    },

    // -------------------------------------------------------------------
    //  /anforderung  – sticker request page (format + self-print vs. ship)
    // -------------------------------------------------------------------
    anforderung_back: {
        de: "Zurück",
        en: "Back",
    },
    anforderung_title: {
        de: "Aufkleber kostenlos anfordern",
        en: "Request your free sticker",
    },
    anforderung_subtitle: {
        de: "Wähle dein Wunschformat – wir senden dir den Aufkleber als PDF zum Selbstdrucken oder bequem per Post.",
        en: "Pick your preferred format – we'll send you the printable PDF or ship it to you free of charge.",
    },
    anforderung_format_title: {
        de: "1. Format auswählen",
        en: "1. Choose a format",
    },
    anforderung_format_sub: {
        de: "Welche Größe brauchst du? A4 passt an jede Tür, A2 wirkt im großen Eingangsbereich.",
        en: "What size do you need? A4 fits on any door, A2 stands out in a large lobby.",
    },
    anforderung_format_A2_desc: {
        de: "Empfang & Halle",
        en: "Lobby & hall",
    },
    anforderung_format_A3_desc: {
        de: "Flure & Treppenhäuser",
        en: "Hallways & stairwells",
    },
    anforderung_format_A4_desc: {
        de: "Türen & Arbeitsplätze",
        en: "Doors & workplaces",
    },
    anforderung_format_continue: {
        de: "Weiter",
        en: "Continue",
    },
    anforderung_format_chosen: {
        de: "Format",
        en: "Format",
    },
    anforderung_method_self_title: {
        de: "Ich drucke meinen Aufkleber selbst",
        en: "I'll print my sticker myself",
    },
    anforderung_method_self_body: {
        de: "Sofort verfügbar: Wir schicken dir die druckfertige PDF-Datei per E-Mail – aufkleben kannst du in wenigen Minuten.",
        en: "Available instantly: we'll e-mail you the print-ready PDF – you can stick it up within minutes.",
    },
    anforderung_method_ship_title: {
        de: "Bitte schickt mir den Aufkleber",
        en: "Please ship the sticker to me",
    },
    anforderung_method_ship_body: {
        de: "Kostenlos & professionell gedruckt – wir senden den Aufkleber per Post an deine Adresse.",
        en: "Free of charge & professionally printed – we'll mail the sticker to your address.",
    },
    anforderung_back_to_method: {
        de: "Andere Methode wählen",
        en: "Choose a different method",
    },
    anforderung_self_title: {
        de: "PDF per E-Mail erhalten",
        en: "Get the PDF by e-mail",
    },
    anforderung_self_body: {
        de: "Trage deine E-Mail-Adresse ein – wir senden dir den Aufkleber im Format {format} als druckfertige PDF-Datei.",
        en: "Enter your e-mail address – we'll send you the sticker as a print-ready PDF in size {format}.",
    },
    anforderung_field_email: {
        de: "E-Mail-Adresse",
        en: "E-mail address",
    },
    anforderung_field_email_placeholder: {
        de: "E-Mail-Adresse *",
        en: "E-mail address *",
    },
    anforderung_self_consent: {
        de: "Ich bin damit einverstanden, dass meine E-Mail-Adresse zur Zusendung des Aufklebers und gelegentlicher Produkt-Updates verwendet wird. Abmeldung jederzeit möglich.",
        en: "I agree that my e-mail address may be used to send me the sticker and occasional product updates. I can unsubscribe at any time.",
    },
    anforderung_self_submit: {
        de: "PDF jetzt zusenden",
        en: "Send PDF now",
    },
    anforderung_self_done_title: {
        de: "PDF wird heruntergeladen!",
        en: "PDF download started!",
    },
    anforderung_self_done_body: {
        de: "Dein personalisierter Aufkleber wurde erzeugt und wird gerade als PDF-Datei heruntergeladen. Falls der Download nicht startet, prüfe bitte deine Browser-Einstellungen oder Pop-up-Blocker.",
        en: "Your personalised sticker has been generated and is being downloaded as a PDF right now. If the download doesn't start, please check your browser settings or pop-up blocker.",
    },
    anforderung_ship_title: {
        de: "Lieferadresse angeben",
        en: "Enter delivery address",
    },
    anforderung_ship_body: {
        de: "Wir drucken deinen Aufkleber im Format {format} und schicken ihn dir kostenlos per Post.",
        en: "We'll print your sticker in size {format} and ship it to you free of charge.",
    },
    anforderung_ship_submit: {
        de: "Aufkleber kostenlos anfordern",
        en: "Request sticker free",
    },
    anforderung_ship_done_title: {
        de: "Bestellung eingegangen!",
        en: "Order received!",
    },
    anforderung_ship_done_body: {
        de: "Vielen Dank! Dein Aufkleber wird gedruckt und in den nächsten Tagen an deine Adresse versendet. Eine Bestätigung erhältst du per E-Mail.",
        en: "Thank you! Your sticker will be printed and shipped within the next few days. You'll receive a confirmation by e-mail.",
    },
    anforderung_error_email: {
        de: "Bitte eine gültige E-Mail-Adresse eintragen.",
        en: "Please enter a valid e-mail address.",
    },
    anforderung_error_consent: {
        de: "Bitte die Einwilligung bestätigen.",
        en: "Please confirm consent.",
    },
    anforderung_error_save: {
        de: "Speichern fehlgeschlagen – bitte erneut versuchen.",
        en: "Saving failed – please try again.",
    },
    anforderung_back_home: {
        de: "Zur Startseite",
        en: "Back to home",
    },

    // -------------------------------------------------------------------
    //  Persistent sticker toast (visible on every page except /anforderung)
    // -------------------------------------------------------------------
    sticker_toast_text: {
        de: "Ja, ich will einen solchen Aufkleber für mein Plakat haben",
        en: "Yes, I want a sticker like this for my poster",
    },
    sticker_toast_cta: {
        de: "Jetzt anfordern",
        en: "Request now",
    },

    // -------------------------------------------------------------------
    //  Demo card on the emergency home page (shown when no QR ?id= present)
    // -------------------------------------------------------------------
    landing_demo_card_title: {
        de: "Demo ausprobieren (Kein Notfall)",
        en: "Try demo (No emergency)",
    },
    landing_demo_card_sub: {
        de: "Starten Sie den Demo-Modus und probieren Sie den Assistenten aus.",
        en: "Start demo mode and try out the assistant.",
    },
    landing_werksnotruf_label: {
        de: "Werksnotruf",
        en: "On-site emergency",
        tr: "Şirket acil hattı",
        uk: "Виробничий екстрений номер",
        hr: "Interni hitni broj",
        bg: "Вътрешен спешен номер",
        ru: "Внутренний экстренный номер",
        ro: "Urgență internă",
        pl: "Wewnętrzny numer alarmowy",
        sq: "Numri i brendshëm i urgjencës",
    },
};

// Merge fr+it translations from translations_extra.js so adding more
// languages stays a one-file change.
import { EXTRA_LANG_TRANSLATIONS } from "./translations_extra";
for (const [key, langs] of Object.entries(EXTRA_LANG_TRANSLATIONS)) {
    if (!translations[key]) {
        // Source key missing – skip silently to avoid hard breaks at runtime.
        continue;
    }
    Object.assign(translations[key], langs);
}

// Merge batch 2 (da, nl, fi, et, el, ga).
import { EXTRA_LANG_TRANSLATIONS_2 } from "./translations_extra_2";
for (const [key, langs] of Object.entries(EXTRA_LANG_TRANSLATIONS_2)) {
    if (!translations[key]) {
        continue;
    }
    Object.assign(translations[key], langs);
}

// Merge batch 3 (lv, lt, mt, pt, sv, sk).
import { EXTRA_LANG_TRANSLATIONS_3 } from "./translations_extra_3";
for (const [key, langs] of Object.entries(EXTRA_LANG_TRANSLATIONS_3)) {
    if (!translations[key]) {
        continue;
    }
    Object.assign(translations[key], langs);
}

// Merge batch 4 (sl, es, cs, hu).
import { EXTRA_LANG_TRANSLATIONS_4 } from "./translations_extra_4";
for (const [key, langs] of Object.entries(EXTRA_LANG_TRANSLATIONS_4)) {
    if (!translations[key]) {
        continue;
    }
    Object.assign(translations[key], langs);
}

export const LANGUAGE_NAMES = LANGUAGES.reduce((acc, l) => {
    acc[l.code] = l.label;
    return acc;
}, {});
